import { useEffect, useRef, useState } from "react";
import {
  AppDialogRequest,
  cancelActiveDialog,
  confirmActiveDialog,
  subscribeDialogs,
} from "@/lib/dialogs";

/**
 * Renders the globally queued app dialog (alert / confirm / prompt) as the
 * single replacement for native browser dialogs. Mount once at the app root.
 */
export function AppDialogHost() {
  const [dialog, setDialog] = useState<AppDialogRequest | null>(null);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => subscribeDialogs(setDialog), []);

  // Reset the prompt input for each newly shown dialog.
  useEffect(() => {
    if (!dialog) return;
    setInputValue(dialog.initialText ?? "");
    const focusTimer = window.setTimeout(() => {
      if (dialog.kind === "prompt") {
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [dialog]);

  // Native-dialog keyboard semantics: Enter = confirm, Escape = cancel.
  useEffect(() => {
    if (!dialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancelActiveDialog();
      } else if (e.key === "Enter") {
        e.preventDefault();
        confirmActiveDialog(inputRef.current?.value ?? inputValue);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog]);

  if (!dialog) return null;

  return (
    <div
      className="app-dialog-overlay suite-ui-dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={dialog.title}
    >
      <div className={`app-dialog suite-ui-dialog app-dialog-${dialog.variant}`}>
        <h3 className="app-dialog-title suite-ui-dialog-header">{dialog.title}</h3>
        <div className="app-dialog-content suite-ui-dialog-body">
          <p className="app-dialog-message">{dialog.message}</p>
          {dialog.kind === "prompt" && (
            <input
              ref={inputRef}
              className="app-dialog-input"
              value={inputValue}
              placeholder={dialog.placeholder}
              onChange={(e) => setInputValue(e.target.value)}
            />
          )}
        </div>
        <div className="app-dialog-actions suite-ui-dialog-footer">
          {dialog.cancelLabel && (
            <button type="button" className="app-dialog-btn" onClick={cancelActiveDialog}>
              {dialog.cancelLabel}
            </button>
          )}
          <button
            type="button"
            autoFocus
            className={`app-dialog-btn app-dialog-btn-primary ${
              dialog.variant === "error" ? "app-dialog-btn-destructive" : ""
            }`}
            onClick={() => confirmActiveDialog(inputValue)}
          >
            {dialog.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
